import axios from 'axios';
import { AsyncAction } from '..';
import { reportFactory } from '../../types/factory';
import { Report } from '../../types/report';

type State = {
    fetching: boolean;
    status: 'idle' | 'updating' | 'success' | 'fail';
    report?: Report;
    changed?: Report;
    changes: { date: Date; change: Partial<Report> }[];
    error?: Error;
};

type Action =
    | { type: 'report/reset' }
    | { type: 'report/status'; payload: { status: State['status'] } }
    | { type: 'report/set'; payload?: { report: Report }; error?: Error }
    | { type: 'report/edit'; payload: { change: Partial<Report> } }
    | { type: 'report/commit' }
    | { type: 'report/discard'; payload: { all: boolean } };

const initialState: State = {
    fetching: false,
    status: 'idle',
    changes: [],
};

/**
 * Apply `changes` to an `report` object returning a new object.
 *
 * @param report the base report
 * @param changes changes to be applied
 * @returns updated object
 */
const withChanges = <T extends Report | Partial<Report>>(report?: T, changes?: Partial<Report>) =>
    ({ ...report, ...changes } as T);

export const reducer = (state = initialState, action: Action): State => {
    switch (action.type) {
        case 'report/reset': {
            return { ...initialState };
        }
        case 'report/status': {
            return { ...state, ...action.payload };
        }
        case 'report/set': {
            if (action.payload)
                return { ...state, fetching: false, ...action.payload, changed: action.payload.report, changes: [] };
            if (action.error) return { ...state, fetching: false, error: action.error };
            return { ...state, fetching: true };
        }
        case 'report/edit': {
            if (!state.report) return { ...state, error: Error('cannot edit undefined report') };
            const changes = [...state.changes, { date: new Date(), ...action.payload }];
            const changed = changes.reduce((acc, c) => withChanges(acc, c.change), state.report);
            return { ...state, changes, changed };
        }
        case 'report/commit': {
            if (!state.report) return { ...state, error: Error('cannot commit undefined report') };
            return { ...state, status: 'idle', report: state.changed, changes: [] };
        }
        case 'report/discard': {
            if (!state.report) return { ...state, error: Error('cannot discard undefined report') };
            if (action.payload.all) state.changes = [];
            else state.changes.pop();
            const changed = state.changes.reduce((acc, c) => withChanges(acc, c.change), state.report);
            return { ...state, changed };
        }
        default:
            return state;
    }
};

/**
 * Reset the reducer to the initial state.
 */
const reset = (): Action => ({ type: 'report/reset' });

/**
 * Create a new report locally.
 *
 * @param rethrow throw the internal error
 */
const create = (rethrow = false): AsyncAction => async dispatch => {
    try {
        dispatch({ type: 'report/status', payload: { status: 'idle' } });
        dispatch({ type: 'report/set' });
        dispatch({ type: 'report/set', payload: { report: reportFactory() } });
    } catch (error) {
        dispatch({ type: 'report/set', error });
        if (rethrow) throw error;
    }
};

/**
 * Get an existing report from the backend.
 *
 * @param id report id
 * @param rethrow throw the internal error
 */
const get = (id: number, rethrow = false): AsyncAction => async dispatch => {
    try {
        dispatch({ type: 'report/status', payload: { status: 'idle' } });
        dispatch({ type: 'report/set' });
        const cancel = axios.CancelToken.source();
        dispatch({ type: 'cancel/set', payload: { 'report/set': cancel } });
        const response = await axios.get<Report>(`/reports/${id}/`, { cancelToken: cancel.token });
        dispatch({ type: 'report/set', payload: { report: response.data } });
    } catch (error) {
        if (axios.isCancel(error)) return;
        dispatch({ type: 'report/set', error });
        if (rethrow) throw error;
    }
};

/**
 * Update the local report to the backend.
 *
 * @param rethrow throw the internal error
 */
const update = (rethrow = false): AsyncAction => async (dispatch, getState) => {
    const { report } = getState();
    try {
        dispatch({ type: 'report/status', payload: { status: 'updating' } });
        dispatch({ type: 'report/set' });
        if (!report.report) throw Error('report not available in redux store');
        const cancel = axios.CancelToken.source();
        dispatch({ type: 'cancel/set', payload: { 'report/set': cancel } });
        const action = report.report.id === -1 ? axios.post : axios.put;
        const url = report.report.id === -1 ? '/reports/' : `/reports/${report.report.id}/`;
        const response = await action<Report>(url, { ...report.report }, { cancelToken: cancel.token });
        dispatch({ type: 'report/set', payload: { report: response.data } });
        dispatch({ type: 'report/status', payload: { status: 'success' } });
    } catch (error) {
        if (axios.isCancel(error)) return;
        dispatch({ type: 'report/set', error });
        dispatch({ type: 'report/status', payload: { status: 'fail' } });
        if (rethrow) throw error;
    }
};

/**
 * Update the report information locally, but without committing it.
 *
 * @param change report object changes
 */
const edit = (change: Partial<Report>): Action => ({ type: 'report/edit', payload: { change } });

/**
 * Commit the edited report, the old report information cannot be restored.
 */
const commit = (): Action => ({ type: 'report/commit' });

/**
 * Discard the edited report.
 *
 * @param all if should discard all changes, not only the last
 */
const discard = (all = false): Action => ({ type: 'report/discard', payload: { all } });

/**
 * Delete the local report from the backend.
 * If the report is only local, the reducer will just reset.
 *
 * @param rethrow throw the internal error
 */
const delete_ = (rethrow = false): AsyncAction => async (dispatch, getState) => {
    const { report } = getState();
    try {
        dispatch({ type: 'report/status', payload: { status: 'updating' } });
        if (!report.report) throw Error('report not available in redux store');
        const cancel = axios.CancelToken.source();
        dispatch({ type: 'cancel/set', payload: { 'report/set': cancel } });
        if (report.report.id !== -1) await axios.delete(`/reports/${report.report.id}/`, { cancelToken: cancel.token });
        dispatch({ type: 'report/reset' });
        dispatch({ type: 'report/status', payload: { status: 'idle' } });
    } catch (error) {
        if (axios.isCancel(error)) return;
        dispatch({ type: 'report/status', payload: { status: 'fail' } });
        if (rethrow) throw error;
    }
};

export const creators = { reset, create, get, update, edit, commit, discard, delete: delete_ };
